import React, { useEffect, useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, Eye, Edit, Trash2, MoreHorizontal, Search, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../components/lib/utils';
import { motion, useAnimation } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Input } from '../../../components/ui/input';
import { getExamResults } from '../../../services/apiclient';
import ExamUserDetailSidebar from './sidebar';

interface Validation {
  isCorrect: boolean;
  score: number;
  feedback: string;
  _id: string;
}

interface ExamCandidate {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  college: string;
  questions: string[];
  answers: string[];
  totalScore: number;
  status: string;
  neuralPatterns: string;
  validations: Validation[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface CandidatesTableProps {
  candidates: ExamCandidate[];
  setCandidates: (candidates: ExamCandidate[]) => void;
  onCandidateSelect: (candidate: ExamCandidate) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  onStatusChange: (candidate: ExamCandidate, newStatus: string, comment?: string) => void;
  candidatesLoading: boolean;
}

const CandidatesTable: React.FC<CandidatesTableProps> = ({
  candidates,
  setCandidates,
  onCandidateSelect,
  sortBy,
  sortOrder,
  onSort,
  onStatusChange,
  candidatesLoading
}) => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState<any[]>([]);
  const [selectedCandidateName, setSelectedCandidateName] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [actionCandidate, setActionCandidate] = useState<ExamCandidate | null>(null);
  const [progress, setProgress] = useState(0);
  const controls = useAnimation();
  const DURATION = 1;

  useEffect(() => {
    console.log('Candidates array:', candidates);
  }, [candidates]);

  useEffect(() => {
    if (isDialogOpen && actionCandidate?._id) {
      console.log(`Opening exam details for candidate: ${actionCandidate.fullName}`);
      setProgress(0);
      const target = selectedCandidateDetails.length || 1;
      let start = 0;
      const step = () => {
        start += 1;
        setProgress(start);
        if (start < target) {
          setTimeout(step, (DURATION * 1000) / target);
        }
      };
      if (target > 0) {
        step();
      }
    } else {
      setProgress(0);
    }
  }, [isDialogOpen, actionCandidate, selectedCandidateDetails]);

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  const handleViewCandidate = async (candidate: ExamCandidate) => {
    try {
      const details = [
        { type: 'Score', value: candidate.totalScore || 'N/A' },
        { type: 'Status', value: candidate.status },
        { type: 'Neural Patterns', value: candidate.neuralPatterns },
      ];
      setSelectedCandidateName(candidate.fullName);
      setSelectedCandidateDetails(details);
      setActionCandidate(candidate);
      setIsDialogOpen(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch candidate details');
    }
  };

  const handleStatusChange = (candidate: ExamCandidate) => {
    setActionCandidate(candidate);
    setSelectedCandidateId(candidate._id);
    const targetStatus = candidate.status === 'completed' ? 'pending' : 'completed';
    setNewStatus(targetStatus);
    if (targetStatus === 'completed') {
      setComment('');
      setIsCommentDialogOpen(true);
    } else {
      setIsConfirmDialogOpen(true);
    }
  };

  const confirmStatusChange = async () => {
    if (!selectedCandidateId || !newStatus || !actionCandidate) return;
    try {
      await onStatusChange(actionCandidate, newStatus, comment || 'Status updated from Candidates Table');
      toast.success(`Candidate status updated to ${newStatus}`);
      setIsCommentDialogOpen(false);
      setIsConfirmDialogOpen(false);
      setComment('');
      setSelectedCandidateId(null);
      setNewStatus(null);
      setActionCandidate(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update candidate status');
    }
  };

  const handleEditCandidate = (candidate: ExamCandidate) => {
    navigate(`/edit-candidate/${candidate._id}`);
  };

  const handleDeleteCandidate = (candidate: ExamCandidate) => {
    console.log(`Delete candidate with ID: ${candidate._id}`);
    toast.success('Candidate deleted successfully');
    setCandidates(candidates.filter(c => c._id !== candidate._id));
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'validated':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'questions_generated':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        'px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  const renderActionsDropdown = (candidate: ExamCandidate) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleViewCandidate(candidate)}>
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleEditCandidate(candidate)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit Candidate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleDeleteCandidate(candidate)}>
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Candidate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleStatusChange(candidate)}>
          <MoreHorizontal className="mr-2 h-4 w-4" />
          {candidate.status === 'completed' ? 'Mark as Pending' : 'Mark as Completed'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="bg-background relative" style={{ marginTop: '0px' }}>
      <div className="h-[calc(100vh-200px)]" style={{ maxWidth: '100vw', overflow: 'scroll' }}>
        <table className="min-w-full divide-y divide-border">
          <thead style={{ backgroundColor: '#FFF5F2' }}>
            <tr>
              <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
              <SortableHeader column="fullName" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                Name
              </SortableHeader>
              <SortableHeader column="email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
              <SortableHeader column="phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
              <SortableHeader column="college" className="bg-[#FFF5F2] min-w-48">College</SortableHeader>
              <SortableHeader column="status" className="bg-[#FFF5F2] min-w-32">Status</SortableHeader>
              <SortableHeader column="totalScore" className="bg-[#FFF5F2] min-w-32">Score</SortableHeader>
              <SortableHeader column="createdAt" className="bg-[#FFF5F2] min-w-40">Created At</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                Actions
              </th>
            </tr>
          </thead>
          {candidatesLoading ? (
            <div className="relative top-[23vh] left-[50vh]">
              <p className="flex items-center gap-2 text-gray-600">
                <svg className="animate-spin h-5 w-5 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                Loading...
              </p>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No candidates found matching your criteria.</p>
            </div>
          ) : (
            candidates.map((candidate) => (
              <tbody className="bg-background divide-y divide-border" key={candidate._id}>
                <tr className={cn('hover:bg-muted/30 transition-colors cursor-pointer')} onClick={() => onCandidateSelect(candidate)}>
                  <td className="px-4 py-4"></td>
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border relative group">
                    <div className="flex items-center gap-3">
                      <span className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary">
                        {candidate.fullName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">
                    <span
                      className="cursor-pointer hover:text-orange-600"
                      title="Copy email"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(candidate.email);
                        toast.success('Email copied!');
                      }}
                    >
                      {candidate.email}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">
                    <span
                      className="cursor-pointer hover:text-orange-600"
                      title="Copy phone number"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(candidate.phone);
                        toast.success('Phone number copied!');
                      }}
                    >
                      {candidate.phone}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">{candidate.college || 'N/A'}</td>
                  <td className="px-4 py-4">
                    <Badge className={getStatusBadgeColor(candidate.status)}>
                      {candidate.status || 'N/A'}
                    </Badge>
                  </td>
                 <td className="px-4 py-4 text-sm text-foreground">
  {candidate.totalScore !== undefined && candidate.totalScore !== null
    ? Number(candidate.totalScore).toFixed(2)
    : "N/A"}
</td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">
                    {new Date(candidate.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">{renderActionsDropdown(candidate)}</td>
                </tr>
              </tbody>
            ))
          )}
        </table>
      </div>

      {/* Exam Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white shadow-lg rounded-lg border border-gray-200 max-w-md px-[16px] py-[25px] gap-2">
          <DialogHeader>
            <DialogTitle className="font-semibold text-lg mb-4 text-gray-800">
              Exam Details for <span className="font-bold text-[#EC6E51]">{selectedCandidateName}</span>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {selectedCandidateDetails.length === 0 ? (
              <p className="text-sm text-gray-500">No details available for {selectedCandidateName}.</p>
            ) : (
              selectedCandidateDetails.map((detail, index) => (
                <div key={index} className="relative flex items-start border-b border-gray-100 pb-4 last:border-b-0">
                  <div className="relative flex items-center justify-center w-6 h-6 mr-4">
                    <motion.div
                      className={`w-4 h-4 z-10 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${
                        index < progress ? 'bg-[#ED795F] border-[#ED795F] text-white' : 'bg-white border-gray-300'
                      }`}
                      initial={{ scale: 1, opacity: 1 }}
                      animate={{ scale: index < progress ? [1, 1.2, 1] : 1, opacity: index < progress ? [1, 0.8, 1] : 1 }}
                      transition={{ duration: 0.8 }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-semibold text-sm ${index < progress ? 'text-gray-800' : 'text-gray-400'}`}>
                        {detail.type}
                      </span>
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Value:</span>
                        <span className={`${index < progress ? 'text-gray-600' : 'text-gray-400'} font-medium`}>
                          {detail.value}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog for Setting as Completed */}
      <Dialog open={isCommentDialogOpen} onOpenChange={setIsCommentDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Mark Candidate as Completed</DialogTitle>
            <DialogDescription className="text-gray-600">
              Please provide a comment for marking the candidate's exam as completed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter your comment here..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCommentDialogOpen(false);
                setComment('');
                setSelectedCandidateId(null);
                setNewStatus(null);
                setActionCandidate(null);
              }}
              className="rounded-xl border-gray-200"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmStatusChange}
              disabled={!comment.trim()}
              className="rounded-xl bg-orange-600 text-white hover:bg-orange-700"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Setting as Pending */}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark the candidate <strong>{actionCandidate?.fullName}</strong>'s status as Pending?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsConfirmDialogOpen(false);
              setSelectedCandidateId(null);
              setNewStatus(null);
              setActionCandidate(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatusChange}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default function ExamResults() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [candidates, setCandidates] = useState<ExamCandidate[]>([]);
  const [sortBy, setSortBy] = useState('fullName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState<ExamCandidate | null>(null);
  const navigate = useNavigate();
useEffect(() => {
console.log("selectedCandidate",selectedCandidate)    
},[selectedCandidate])

  const fetchCandidates = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getExamResults({ page: currentPage.toString(), limit: 10, search: searchTerm });
      const apps = response.users as unknown as ExamCandidate[];
      setCandidates(apps);
      setTotalPages(response.totalPages || 1);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch candidates');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedCandidate(null); // Clear selected candidate when changing pages
    }
  };

  const handleStatusChange = async (candidate: ExamCandidate, newStatus: string, comment?: string) => {
    setCandidates(candidates.map(c =>
      c._id === candidate._id ? { ...c, status: newStatus } : c
    ));
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2 justify-end pr-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePageChange(page)}
            disabled={isLoading}
            className={currentPage === page ? 'bg-[#EC6E51] text-primary-foreground' : ''}
          >
            {page}
          </Button>
        ))}
        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={isLoading}
            >
              {totalPages}
            </Button>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4">
        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={handleBackNavigation}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Exam Candidates</h1>
            </div>
          </div>
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search Candidates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full sm:w-64 border-gray-300"
            />
          </div>
        </div>
      </div>
      <div className="flex">
        <CandidatesTable
          candidates={candidates}
          setCandidates={setCandidates}
          onCandidateSelect={(candidate) => setSelectedCandidate(candidate)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onStatusChange={handleStatusChange}
          candidatesLoading={isLoading}
        />
        {selectedCandidate && (
         <ExamUserDetailSidebar user={selectedCandidate} onClose={() => setSelectedCandidate(null)}/>
        )}
      </div>
      <div className="flex items-center gap-2 justify-end pr-4">
        {renderPagination()}
      </div>
    </div>
  );
}